import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  HIERARCHY_TIERS,
  USER_STATUS_LABELS,
  chainToSettings,
  defaultRoleName,
  isStaffNode,
  nodeOf,
  reportsTo,
  resolveChain,
  type ClonableTier,
  type HierarchyTier,
  type OrganizationDto,
  type TierLevels,
  type UserStatus,
} from "@gatiq/shared";
import { Link, useNavigate, useParams } from "react-router";
import { ChevronLeft, ChevronRight, ExternalLink, Factory, KeyRound, Layers, UserPlus } from "lucide-react";
import { chosenRoleNames, HierarchyDragDropBuilder, TIER_DEFINITIONS } from "@/components/HierarchyDragDropBuilder";
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  Modal,
  Page,
  PageBody,
  PageHeader,
  Skeleton,
  StatCard,
  Switch,
  Table,
  TD,
  TDStacked,
  TH,
  THead,
  RowActions,
  TR,
  statusTone,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { useLanguage } from "@/i18n/language";
import { AddOrgAdminModal } from "./AddOrgAdminModal";

/** Mirrors `OrganizationDetailDto` from `apps/api/src/services/organizations.ts`. */
interface OrgAdmin {
  id: string;
  name: string;
  email: string | null;
  // The API sends a UserStatus; typing it as a bare string here let a raw
  // enum reach the screen with nothing to catch it.
  status: UserStatus;
  displayId: string | null;
  createdAt: string;
}

interface OrgDetail {
  id: string;
  name: string;
  slug: string;
  status: "active" | "suspended";
  logo: string | null;
  color1: string | null;
  adminEmail: string | null;
  totalUsers: number;
  /** Modules made available to this org (D-20). */
  features?: { dispatch?: boolean };
  settings?: {
    karigarApprovalMode?: string;
    distributorEnabled?: boolean;
    enabledTiers?: HierarchyTier[];
    tierLevels?: TierLevels;
    roles?: Record<string, { name?: string }>;
    chainOrder?: string[];
    commission?: {
      counterFixedPoints?: number;
      distributorFixedPoints?: number;
    };
  };
  counts: {
    salesReps: number;
    counters: number;
    karigars: number;
    pendingApprovals: number;
  };
  createdAt: string;
  admins: OrgAdmin[];
}

type ConfirmAction = "suspend" | "activate" | "delete" | null;


const dateLong = (iso: string) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

/**
 * One tenant, seen by the platform: who it is, how big it is, how its
 * distribution chain is shaped, and who can sign in to run it.
 */
export function OrganizationDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { refresh } = useAuth();
  const { t } = useLanguage();

  const [org, setOrg] = useState<OrgDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState<ConfirmAction>(null);
  const [busy, setBusy] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);
  const [editTiersOpen, setEditTiersOpen] = useState(false);
  const [savingTiers, setSavingTiers] = useState(false);
  // The chain being edited, top to bottom, copies included (D-16).
  const [draftChain, setDraftChain] = useState<string[]>([]);
  const [roleNames, setRoleNames] = useState<Record<string, string>>({});

  const [savingFeature, setSavingFeature] = useState(false);

  async function setDispatchAvailable(next: boolean): Promise<void> {
    if (!org) return;
    setSavingFeature(true);
    try {
      await api.patch(`/platform/orgs/${org.id}`, { features: { dispatch: next } });
      setOrg({ ...org, features: { ...org.features, dispatch: next } });
      toast.success(next ? "Dispatch is now available to this organization." : "Dispatch is no longer available.");
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not change this.");
    } finally {
      setSavingFeature(false);
    }
  }

  const [addAdminOpen, setAddAdminOpen] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await api.get<OrgDetail>(`/platform/orgs/${orgId}`);
      setOrg(res);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load this organization.");
    } finally {
      setLoading(false);
    }
  }, [orgId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleStatusChange(status: "active" | "suspended") {
    if (!org) return;
    setBusy(true);
    try {
      const updated = await api.patch<{ status: "active" | "suspended" }>(
        `/platform/orgs/${org.id}/status`,
        { status },
      );
      setOrg((prev) => (prev ? { ...prev, status: updated.status } : prev));
      toast.success(status === "suspended" ? "Organization suspended." : "Organization activated.");
      setConfirm(null);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not update the organization.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!org) return;
    setBusy(true);
    try {
      await api.del(`/platform/orgs/${org.id}`);
      toast.success(`${org.name} was deleted.`);
      navigate("/super-admin/organizations");
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not delete the organization.");
      setBusy(false);
      setConfirm(null);
    }
  }

  async function handleLoginAs(admin: OrgAdmin) {
    if (!org) return;
    setImpersonatingId(admin.id);
    try {
      await api.post(`/platform/impersonate/${admin.id}`);
      await refresh();
      navigate(`/${org.slug}/admin`);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not sign in as this admin.");
    } finally {
      setImpersonatingId(null);
    }
  }

  function openChainEditor(): void {
    if (!org) return;
    setDraftChain(resolveChain(org.settings));
    setRoleNames(
      Object.fromEntries(
        Object.entries(org.settings?.roles ?? {}).flatMap(([key, role]) => (role.name ? [[key, role.name]] : [])),
      ),
    );
    setEditTiersOpen(true);
  }

  async function handleSaveTiers() {
    if (!org) return;
    setSavingTiers(true);
    try {
      const updated = await api.patch<OrganizationDto>(`/platform/orgs/${org.id}`, {
        ...chainToSettings(draftChain),
        roleNames: chosenRoleNames(roleNames),
      });
      setOrg((prev) =>
        prev
          ? {
              ...prev,
              settings: {
                ...prev.settings,
                enabledTiers: updated.settings.enabledTiers,
                tierLevels: updated.settings.tierLevels,
                chainOrder: updated.settings.chainOrder,
                roles: updated.settings.roles,
                distributorEnabled: updated.settings.distributorEnabled,
              },
            }
          : prev,
      );
      toast.success("Distribution chain updated.");
      setEditTiersOpen(false);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not update the distribution chain.");
    } finally {
      setSavingTiers(false);
    }
  }

  const back = (
    <Link
      className="inline-flex items-center gap-1 text-[13px] text-muted transition-colors hover:text-ink"
      to="/super-admin/organizations"
    >
      <ChevronLeft aria-hidden="true" className="size-4" />
      Organizations
    </Link>
  );

  if (loading && !org) {
    return (
      <Page>
        <PageHeader title="Organization" />
        <PageBody className="flex flex-col gap-4">
          <Skeleton className="h-28 w-full" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton className="h-24" key={i} />
            ))}
          </div>
          <Skeleton className="h-48 w-full" />
        </PageBody>
      </Page>
    );
  }

  if (!org) {
    return (
      <Page>
        <PageHeader title="Organization not found" />
        <Card>
          <EmptyState
            action={back}
            message="It may have been deleted, or the link is out of date."
            title="This organization does not exist"
          />
        </Card>
      </Page>
    );
  }

  const chain = resolveChain(org.settings);
  const unused = HIERARCHY_TIERS.filter((tier) => !chain.some((node) => nodeOf(node).tier === tier));
  /** A sales role's own name ("Field Officer"), or its default ("Sales Rep 2"). */
  const stepName = (node: string): string => {
    const { tier, level } = nodeOf(node);
    if (!isStaffNode(node)) return t(TIER_DEFINITIONS[tier].hindiLabel, TIER_DEFINITIONS[tier].label);
    const levels = chain.filter((n) => nodeOf(n).tier === tier).length;
    return org.settings?.roles?.[node]?.name || defaultRoleName(tier as ClonableTier, level, levels);
  };
  const portalPath = `/${org.slug}/login`;
  const portalDisplay = `${window.location.host}/${org.slug}`;
  const commission = org.settings?.commission;

  return (
    <Page>
      <PageHeader
        action={
          <div className="flex gap-2">
            {org.status === "active" ? (
              <Button onClick={() => setConfirm("suspend")} size="sm" variant="secondary">
                Suspend
              </Button>
            ) : (
              <Button onClick={() => setConfirm("activate")} size="sm" variant="secondary">
                Activate
              </Button>
            )}
            <Button onClick={() => setConfirm("delete")} size="sm" variant="danger">
              Delete
            </Button>
          </div>
        }
        description="Organization"
        title={org.name}
      />

      <PageBody className="flex flex-col gap-4 pb-2 [&>*]:shrink-0">
        {back}

        {/* ── Identity ── */}
        <Card>
          <div className="flex flex-wrap items-center gap-5 px-5 py-5">
            {org.logo ? (
              <img
                alt=""
                className="size-16 shrink-0 rounded-lg bg-surface object-contain p-2 ring-1 ring-rule"
                src={org.logo}
              />
            ) : (
              <span
                aria-hidden="true"
                className="grid size-16 shrink-0 place-items-center rounded-lg bg-ink/[0.05] font-title text-2xl font-semibold text-ink ring-1 ring-rule"
              >
                {org.name.charAt(0).toUpperCase()}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h2 className="truncate font-title text-xl font-semibold tracking-[-0.015em] text-ink">
                  {org.name}
                </h2>
                <Badge tone={statusTone(org.status)}>{org.status === "active" ? "Active" : "Suspended"}</Badge>
              </div>
              <a
                className="mt-1 inline-flex items-center gap-1 text-sm text-brand-ink hover:underline"
                href={portalPath}
                rel="noreferrer"
                target="_blank"
              >
                {portalDisplay}
                <ExternalLink aria-hidden="true" className="size-3.5" />
              </a>
            </div>
            <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-3">
              <Meta label="Created">{dateLong(org.createdAt)}</Meta>
              <Meta label="Users">{org.totalUsers.toLocaleString("en-IN")}</Meta>
              <Meta label="Brand colour">
                {org.color1 ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      aria-hidden="true"
                      className="size-3.5 rounded-[4px] ring-1 ring-black/10"
                      style={{ background: org.color1 }}
                    />
                    <span className="font-mono text-xs uppercase">{org.color1}</span>
                  </span>
                ) : (
                  <span className="text-muted">Platform default</span>
                )}
              </Meta>
            </dl>
          </div>
        </Card>

        {/* ── Size ── */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Sales reps" value={org.counts.salesReps.toLocaleString("en-IN")} />
          <StatCard label="Counters" value={org.counts.counters.toLocaleString("en-IN")} />
          <StatCard label="Karigars" value={org.counts.karigars.toLocaleString("en-IN")} />
          <StatCard
            hint={org.counts.pendingApprovals > 0 ? "waiting on the org admin" : "nobody waiting"}
            label="Pending approvals"
            tone={org.counts.pendingApprovals > 0 ? "attention" : "neutral"}
            value={org.counts.pendingApprovals.toLocaleString("en-IN")}
          />
        </div>

        {/* ── Chain + settings ── */}
        <div className="grid gap-4 xl:grid-cols-3">
          <Card className="xl:col-span-2">
            <CardHeader
              action={
                <Button icon={<Layers aria-hidden="true" className="size-4" />} onClick={openChainEditor} size="sm" variant="secondary">
                  Edit chain
                </Button>
              }
              title="Distribution chain"
            />
            <div className="px-5 py-5">
              <ol className="flex flex-wrap items-center gap-y-3">
                <ChainNode first icon={<Factory aria-hidden="true" className="size-4" />} label={t("हेड ऑफिस", "Head office")} />
                {chain.map((node, i) => {
                  const Icon = TIER_DEFINITIONS[nodeOf(node).tier].icon;
                  const manager = reportsTo(chain, node);
                  return (
                    <ChainNode
                      icon={<Icon aria-hidden="true" className="size-4" />}
                      key={node}
                      label={stepName(node)}
                      note={manager ? `reports to ${stepName(manager)}` : undefined}
                      step={i + 1}
                    />
                  );
                })}
              </ol>
              {unused.length > 0 ? (
                <p className="mt-4 border-t border-rule/70 pt-3 text-[13px] text-muted">
                  Not used: {unused.map((tier) => t(TIER_DEFINITIONS[tier].hindiLabel, TIER_DEFINITIONS[tier].label)).join(", ")}
                </p>
              ) : null}
            </div>
          </Card>

          <Card>
            <CardHeader title="Programme settings" />
            <dl className="flex flex-col divide-y divide-rule/70 px-5">
              <SettingRow label="Karigar approval">Org admin approves</SettingRow>
              <SettingRow label="Distributor sign-in">
                {org.settings?.distributorEnabled ? "Enabled" : "Off"}
              </SettingRow>
              <SettingRow label="Counter commission">
                {commission?.counterFixedPoints ? `${commission.counterFixedPoints} pts / scan` : "None"}
              </SettingRow>
              <SettingRow label="Dispatch module">
                <span className="inline-flex items-center gap-2.5">
                  <span className="text-xs font-normal text-muted">
                    {org.features?.dispatch ? "Available" : "Not available"}
                  </span>
                  <Switch
                    checked={Boolean(org.features?.dispatch)}
                    disabled={savingFeature}
                    label="Dispatch module available"
                    onChange={(next) => void setDispatchAvailable(next)}
                  />
                </span>
              </SettingRow>
            </dl>
          </Card>
        </div>

        {/* ── Admins ── */}
        <Card>
          <CardHeader
            action={
              <Button onClick={() => setAddAdminOpen(true)} size="sm" variant="secondary">
                <UserPlus aria-hidden="true" className="size-4" />
                Add admin
              </Button>
            }
            description="Every admin here has the same full access to this organization."
            title="Org admins"
          />
          {org.admins.length === 0 ? (
            <EmptyState message="Add an admin so someone can sign in and run the programme." title="No org admins yet" />
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>Admin</TH>
                  <TH>Status</TH>
                  <TH>Added</TH>
                  <TH align="right" width="4.5rem">
                    <span className="sr-only">Actions</span>
                  </TH>
                </tr>
              </THead>
              <tbody>
                {org.admins.map((admin) => (
                  <TR key={admin.id}>
                    <TDStacked
                      avatar={<Avatar name={admin.name} />}
                      primary={admin.name}
                      secondary={[admin.email, admin.displayId].filter(Boolean).join(" · ") || undefined}
                    />
                    <TD>
                      <Badge tone={statusTone(admin.status)}>{USER_STATUS_LABELS[admin.status]}</Badge>
                    </TD>
                    <TD className="text-muted">{dateLong(admin.createdAt)}</TD>
                    <TD align="right">
                      <RowActions
                        busy={impersonatingId === admin.id}
                        items={[
                          {
                            label: "Log in as this admin",
                            icon: KeyRound,
                            disabled: org.status !== "active",
                            onSelect: () => void handleLoginAs(admin),
                          },
                        ]}
                        label={admin.name}
                      />
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </PageBody>

      <AddOrgAdminModal
        onAdded={() => void load()}
        onClose={() => setAddAdminOpen(false)}
        open={addAdminOpen}
        orgId={org.id}
        orgName={org.name}
      />

      <ConfirmDialog
        confirmLabel="Suspend"
        loading={busy}
        message={
          <>
            Suspend <strong>{org.name}</strong>? Every user in this organization will be signed out and
            unable to sign in until it is reactivated.
          </>
        }
        onClose={() => setConfirm(null)}
        onConfirm={() => void handleStatusChange("suspended")}
        open={confirm === "suspend"}
        title="Suspend organization"
      />
      <ConfirmDialog
        confirmLabel="Activate"
        loading={busy}
        message={
          <>
            Activate <strong>{org.name}</strong> again? Its users will be able to sign in.
          </>
        }
        onClose={() => setConfirm(null)}
        onConfirm={() => void handleStatusChange("active")}
        open={confirm === "activate"}
        title="Activate organization"
      />
      <ConfirmDialog
        confirmLabel="Delete"
        loading={busy}
        message={
          <>
            Delete <strong>{org.name}</strong>? This cannot be undone from here — all of its users will be
            deactivated.
          </>
        }
        onClose={() => setConfirm(null)}
        onConfirm={() => void handleDelete()}
        open={confirm === "delete"}
        title="Delete organization"
      />

      <Modal
        footer={
          <>
            <Button onClick={() => setEditTiersOpen(false)} variant="secondary">
              Cancel
            </Button>
            <Button disabled={draftChain.length === 0} loading={savingTiers} onClick={() => void handleSaveTiers()}>
              Save chain
            </Button>
          </>
        }
        onClose={() => setEditTiersOpen(false)}
        open={editTiersOpen}
        title="Edit distribution chain"
        width="lg"
      >
        <HierarchyDragDropBuilder
          names={roleNames}
          onChange={setDraftChain}
          onNamesChange={setRoleNames}
          orgName={org.name}
          value={draftChain}
        />
      </Modal>
    </Page>
  );
}

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}

function SettingRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right font-medium text-ink">{children}</dd>
    </div>
  );
}

/** One step of the chain, with the arrow into it drawn before it. */
function ChainNode({
  icon,
  label,
  note,
  step,
  first = false,
}: {
  icon: ReactNode;
  label: string;
  note?: string | undefined;
  step?: number;
  first?: boolean;
}) {
  return (
    <li className="flex items-center">
      {first ? null : <ChevronRight aria-hidden="true" className="mx-1.5 size-4 shrink-0 text-muted/70" />}
      <span
        className={`inline-flex items-center gap-2 rounded-md py-1.5 pr-3 pl-1.5 text-[13px] ring-1 ring-inset ${
          first ? "bg-brand-light font-semibold text-brand-ink ring-brand-border" : "bg-raised text-ink ring-rule"
        }`}
      >
        <span
          className={`grid size-6 place-items-center rounded ${first ? "bg-brand text-on-brand" : "bg-ink/[0.05] text-muted"}`}
        >
          {icon}
        </span>
        {step ? <span className="font-mono text-[11px] text-muted">{step}</span> : null}
        <span className="font-medium">{label}</span>
        {note ? <span className="text-[11px] text-muted">· {note}</span> : null}
      </span>
    </li>
  );
}
